/**
 * FillInBlankEditor.jsx
 * Admin content editor for lesson_type === 'fill_in_blank'
 *
 * Content shape:
 *   { questions: [{ sentence, answers, correct, mode }] }
 *
 * sentence: "I ___ to the airport." (underscore marks the blank)
 * mode: 'word_pool' | 'text_input'
 * answers: alternative word options shown in word_pool mode
 * correct: the correct answer string
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const MODES = ['word_pool', 'text_input'];
const DEFAULT_QUESTION = { sentence: '', answers: [], correct: '', mode: 'word_pool' };

export default function FillInBlankEditor({ value, onChange }) {
  const { t } = useLanguage();
  const questions = Array.isArray(value?.questions) ? value.questions : [];

  const update = (next) => onChange({ ...value, questions: next });

  const addQuestion = () => update([...questions, { ...DEFAULT_QUESTION }]);

  const removeQuestion = (idx) => update(questions.filter((_, i) => i !== idx));

  const updateQuestion = (idx, field, val) =>
    update(questions.map((q, i) => (i === idx ? { ...q, [field]: val } : q)));

  const addAnswer = (idx) => {
    const q = questions[idx];
    updateQuestion(idx, 'answers', [...(q.answers || []), '']);
  };

  const updateAnswer = (qIdx, aIdx, val) => {
    const q = questions[qIdx];
    const answers = (q.answers || []).map((a, i) => (i === aIdx ? val : a));
    updateQuestion(qIdx, 'answers', answers);
  };

  const removeAnswer = (qIdx, aIdx) => {
    const q = questions[qIdx];
    updateQuestion(qIdx, 'answers', (q.answers || []).filter((_, i) => i !== aIdx));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...questions];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update(next);
  };

  const moveDown = (idx) => {
    if (idx === questions.length - 1) return;
    const next = [...questions];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update(next);
  };

  return (
    <div className="space-y-3">
      {questions.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {questions.map((q, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4 space-y-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === questions.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeQuestion(idx)}
                aria-label={t('lessonEditor.fillInBlank.removeQuestion')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Sentence */}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
              {t('lessonEditor.fillInBlank.sentence')} *
              <span className="ml-1 text-[var(--text-muted)] font-normal">
                ({t('lessonEditor.fillInBlank.sentenceHint')})
              </span>
            </label>
            <Input
              value={q.sentence}
              onChange={(e) => updateQuestion(idx, 'sentence', e.target.value)}
              placeholder={t('lessonEditor.fillInBlank.sentencePlaceholder')}
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {/* Correct answer */}
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.fillInBlank.correct')} *
              </label>
              <Input
                value={q.correct}
                onChange={(e) => updateQuestion(idx, 'correct', e.target.value)}
                placeholder={t('lessonEditor.fillInBlank.correctPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>

            {/* Mode */}
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.fillInBlank.mode')}
              </label>
              <select
                value={q.mode}
                onChange={(e) => updateQuestion(idx, 'mode', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {t(`lessonEditor.fillInBlank.mode_${m}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Alternatives (word_pool mode) */}
          {q.mode === 'word_pool' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-[var(--text-muted)]">
                  {t('lessonEditor.fillInBlank.alternatives')}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addAnswer(idx)}
                  className="rounded-xl border-2 text-xs font-bold h-6 px-2"
                >
                  <Plus size={11} className="mr-1" /> {t('lessonEditor.common.add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(q.answers || []).map((a, ai) => (
                  <div key={ai} className="flex items-center gap-1">
                    <Input
                      value={a}
                      onChange={(e) => updateAnswer(idx, ai, e.target.value)}
                      className="rounded-xl border-2 border-[var(--border-light)] text-xs w-24 h-7 px-2"
                    />
                    <button
                      type="button"
                      onClick={() => removeAnswer(idx, ai)}
                      className="text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
                      aria-label={t('lessonEditor.common.remove')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {(q.answers || []).length === 0 && (
                  <span className="text-xs text-[var(--text-muted)] italic">
                    {t('lessonEditor.fillInBlank.noAlternatives')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addQuestion}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.fillInBlank.addQuestion')}
      </Button>
    </div>
  );
}
